import React from 'react';
import { Box, Button, CardMedia, Container, Divider, Grid, Link, Typography } from '@mui/material';
import slideImage from '../../images/Slide1.png';
import GEMMAmodellingsystem from '../../images/GEMMAmodellingsystem.png';
import deccMap from '../../images/deccMap.png';
import gemini from '../../images/GEMINI_UK_locations_named_20230905.png';
import geminimodelling from '../../images/geminimodellig.png';
import modellingChart from '../../images/modellingChart.png';

const imageSx = {
  width: '100%',
  height: 'auto',
  maxHeight: 360,
  objectFit: 'contain',
  border: '1px solid #CECECE',
  backgroundColor: '#FFFFFF',
};

const Section = ({ id, title, children }: { id: string; title: string; children: React.ReactNode }) => (
  <Box component="section" id={id} sx={{ py: { xs: 4, md: 6 } }}>
    <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: 700 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const AboutUs = () => (
  <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh', pt: { xs: 8, md: 11 } }}>
    <Container maxWidth="lg">
      <Box component="header" sx={{ maxWidth: 820, pb: 3 }}>
        <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: 1.2 }}>
          GEMMA programme
        </Typography>
        <Typography variant="h2" component="h1" sx={{ fontSize: { xs: '2.2rem', md: '3.4rem' }, mb: 2 }}>
          Understanding greenhouse gas emissions in the UK
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, lineHeight: 1.6 }}>
          GEMMA combines atmospheric observations, meteorology and emissions modelling to provide transparent estimates of greenhouse gas sources and sinks.
        </Typography>
      </Box>

      <Box component="nav" aria-label="About GEMMA sections" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, py: 2, borderTop: 1, borderBottom: 1, borderColor: 'divider' }}>
        {[
          ['Programme', '#programme'],
          ['Measurements', '#measurements'],
          ['Modelling', '#modelling'],
        ].map(([label, href]) => (
          <Link key={href} href={href} underline="hover" sx={{ fontWeight: 700, mr: 2 }}>{label}</Link>
        ))}
      </Box>

      <Section id="programme" title="The programme">
        <Grid container spacing={{ xs: 3, md: 6 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography color="text.secondary" sx={{ lineHeight: 1.75 }}>
              GEMMA provides insight into the sources and sinks of greenhouse gases within the UK. It highlights uncertainty in the National GHG Inventory and supports evidence-based mitigation policy and action.
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.75, mt: 2 }}>
              The programme is developing an operational framework to map net zero implementation to 2050 and beyond. Future work will support more frequent outputs with improved geographic, sectoral and temporal resolution.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <CardMedia component="img" image={slideImage} alt="GEMMA observation and modelling programme overview" sx={imageSx} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 5, p: { xs: 2, md: 3 }, backgroundColor: '#FFFFFF', borderLeft: '4px solid', borderColor: 'primary.main' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>GEMMA GHG modelling system</Typography>
          <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
            The system connects observations and models to produce emissions estimates that can be compared with inventory data and examined at national and regional scales.
          </Typography>
          <CardMedia component="img" image={GEMMAmodellingsystem} alt="GEMMA GHG modelling system diagram" sx={{ ...imageSx, mt: 3, maxHeight: 420 }} />
        </Box>
      </Section>

      <Divider />

      <Section id="measurements" title="Greenhouse gas measurements">
        <Grid container spacing={{ xs: 3, md: 6 }}>
          <Grid item xs={12} md={5}>
            <Typography color="text.secondary" sx={{ lineHeight: 1.75 }}>
              High-frequency measurement networks are essential for investigating climate change and verifying emissions targets under the Kyoto, Paris and Montreal protocols.
            </Typography>
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>UK and Ireland observation sites</Typography>
            <Box component="ul" sx={{ pl: 2.5, mt: 0, color: 'text.secondary', lineHeight: 1.8 }}>
              <li>Tacolneston, Weybourne and Ridge Hill</li>
              <li>Mace Head, Ireland</li>
              <li>Bilsdale and Heathfield</li>
              <li>Jodrell Bank and Balruddery</li>
            </Box>
          </Grid>
          <Grid item xs={12} md={7}>
            <CardMedia component="img" image={deccMap} alt="Map of the UK DECC greenhouse gas observation network" sx={imageSx} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.6 }}>
              The UK DECC network has measured atmospheric composition continuously since 1987 at Mace Head, with additional sites added over time. See the <Link href="https://openghg.github.io/decc_dashboard/" target="_blank" rel="noopener noreferrer">DECC dashboard</Link> for measurement data.
            </Typography>
          </Grid>
        </Grid>
        <Grid container spacing={{ xs: 3, md: 6 }} alignItems="center" sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Typography color="text.secondary" sx={{ lineHeight: 1.75 }}>
              A new network of 10 ground-based total-column sun spectrometers is being deployed by the National Centre for Earth Observation. The network will monitor carbon dioxide and methane through the atmospheric column.
            </Typography>
            <Link href="https://www.nceo.ac.uk/our-research/national-programmes/gemini/" target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-block', mt: 2, fontWeight: 700 }}>
              Learn more about the GEMINI network
            </Link>
          </Grid>
          <Grid item xs={12} md={6}>
            <CardMedia component="img" image={gemini} alt="GEMINI UK measurement locations" sx={imageSx} />
          </Grid>
        </Grid>
      </Section>

      <Divider />

      <Section id="modelling" title="Greenhouse gas modelling">
        <Grid container spacing={{ xs: 3, md: 6 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography color="text.secondary" sx={{ lineHeight: 1.75 }}>
              Emissions estimates begin by examining where air has travelled before reaching an observation station. Meteorological fields from the UK Met Office feed the Numerical Atmospheric-dispersion Modelling Environment (NAME), which describes how pollution moves and dilutes through the atmosphere.
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.75, mt: 2 }}>
              NAME runs backwards in time to estimate the previous 30 days of air history at each measurement site. Inversion systems then identify the geographic emissions map that best matches the observed time series.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <CardMedia component="img" image={geminimodelling} alt="GEMMA atmospheric modelling process" sx={imageSx} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 5, p: { xs: 2, md: 3 }, backgroundColor: '#FFFFFF', border: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Modelling systems</Typography>
          <Typography color="text.secondary" sx={{ lineHeight: 1.75 }}>
            GEMMA uses InTEM, produced by the Met Office; RHIME, developed and operated by the University of Bristol; and EnKF, developed and operated by the University of Edinburgh and NCEO. Results are reported for the UK and for geographic areas according to their uncertainty levels.
          </Typography>
          <CardMedia component="img" image={modellingChart} alt="Example methane emissions estimate with uncertainty ranges" sx={{ ...imageSx, mt: 3 }} />
        </Box>
      </Section>

      <Box sx={{ py: 5, textAlign: 'center' }}>
        <Button variant="contained" href="https://www.npl.co.uk/greenhouse-gas-emissions-measurement-modelling" target="_blank" rel="noopener noreferrer">
          More about the programme
        </Button>
      </Box>
    </Container>
  </Box>
);

export default AboutUs;
